import React, { useEffect, useState } from 'react';
import './styles';
import UIListInputRow from '../../../../../components/UI/UIListInputRow';
import { UI_LABELED_INPUT_NUMBER_TYPE, UI_LABELED_INPUT_TEXT_FIELD } from '../../../../../components/UI/UILabeledInput/config';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { GET_LISTING_INFO } from './queries';
import Queries from '../../../../../utils/API/Queries';
import * as strings from './strings';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getIsUserAdminSelector } from '../../../../../redux/selectors/CurrentUserSelectors';

export default function ListingInfo({ listingId }: ListingProps) {
	const [listing, setListing] = useState<ListingType>();
	const [askingPrice, setAskingPrice] = useState<number>();
	const [squareFootage, setSquareFootage] = useState<number>();
	const [beds, setBeds] = useState<number>();
	const [baths, setBaths] = useState<string>();
	const [homeDescription, setHomeDescription] = useState<string>();
	const [neighborhoodDescription, setNeighborhoodDescription] = useState<string>();
	const [isSaving, setIsSaving] = useState<object>({});

	const isUserAdmin = useSelector(getIsUserAdminSelector);

	const [updateListingInfo] = useMutation(Queries.UPDATE_LISTING);

	const [getListing, { data: listingData }] = useLazyQuery(GET_LISTING_INFO, {
		variables: { id: listingId }
	});

	useEffect(() => {
		(async () => {
			await getListing();
		})();
	}, []);

	useEffect(() => {
		if (listingData) {
			setListing(listingData['listing']);
			setAskingPrice(listingData['listing'].askingPrice);
			setSquareFootage(listingData['listing'].squareFootage);
			setBeds(listingData['listing'].beds);
			setBaths(listingData['listing'].baths);
			setHomeDescription(listingData['listing'].homeDescription);
			setNeighborhoodDescription(listingData['listing'].neighborhoodDescription);
		}
	}, [listingData]);

	const updateListing = async (key: string, value: any) => {
		setIsSaving({ ...isSaving, [key]: true });
		let variables = {
			id: listingId,
			[key]: value
		};
		const response = await updateListingInfo({ variables });
		const { updateListing } = response['data'];
		if (updateListing) {
			setIsSaving({ ...isSaving, [key]: false });
			toast.success(strings.infoUpdateSuccess + key + '!');
			await getListing();
		}
	};

	if (!listing) return null;

	const renderListingInfoCard = () => {
		return (
			<>
				<UIListInputRow
					className='listingInfo--inputRow'
					label={'ASKING PRICE'}
					type={UI_LABELED_INPUT_NUMBER_TYPE}
					prefix={'$'}
					onChangeNumberValue={setAskingPrice}
					savedValue={listing.askingPrice}
					isLoading={isSaving['askingPrice']}
					onSave={() => updateListing('askingPrice', askingPrice)}
					value={askingPrice}
					placeholder={strings.askingPricePlaceholder}
				/>
				<UIListInputRow
					className='listingInfo--inputRow'
					label={strings.sqLabel}
					type={UI_LABELED_INPUT_NUMBER_TYPE}
					onChangeNumberValue={setSquareFootage}
					savedValue={listing.squareFootage ? listing.squareFootage.toString() : listing.squareFootage}
					isLoading={isSaving['squareFootage']}
					onSave={() => updateListing('squareFootage', squareFootage)}
					value={squareFootage ? squareFootage.toString() : squareFootage}
					placeholder={strings.sqPlaceholder}
				/>
			</>
		);
	};

	const renderPropertyInfoCard = () => {
		if (!isUserAdmin) return null;
		return (
			<>
				<UIListInputRow
					className='listingInfo--inputRow'
					label={strings.bedsLabel}
					type={UI_LABELED_INPUT_NUMBER_TYPE}
					onChangeNumberValue={setBeds}
					savedValue={listing.beds ? listing.beds.toString() : listing.beds}
					isLoading={isSaving['beds']}
					onSave={() => updateListing('beds', beds)}
					value={beds ? beds.toString() : beds}
					placeholder={strings.bedsPlaceholder}
				/>
				<UIListInputRow
					className='listingInfo--inputRow'
					label={strings.bathsLabel}
					type={UI_LABELED_INPUT_NUMBER_TYPE}
					onChangeValue={setBaths}
					savedValue={listing.baths ? listing.baths.toString() : listing.baths}
					isLoading={isSaving['baths']}
					onSave={() => updateListing('baths', baths)}
					value={baths ? baths.toString() : baths}
					placeholder={strings.bathsPlaceholder}
				/>
				<UIListInputRow
					className='listingInfo--inputRow'
					label={strings.neighborhoodDescriptionLabel}
					type={UI_LABELED_INPUT_TEXT_FIELD}
					onChangeValue={setNeighborhoodDescription}
					savedValue={listing.neighborhoodDescription}
					isLoading={isSaving['neighborhoodDescription']}
					onSave={() => updateListing('neighborhoodDescription', neighborhoodDescription)}
					value={neighborhoodDescription}
					placeholder={strings.neighborhoodDescriptionPlaceholder}
				/>
				<UIListInputRow
					className='listingInfo--inputRow'
					label={strings.homeDescriptionLabel}
					type={UI_LABELED_INPUT_TEXT_FIELD}
					onChangeValue={setHomeDescription}
					savedValue={listing.homeDescription}
					isLoading={isSaving['homeDescription']}
					onSave={() => updateListing('homeDescription', homeDescription)}
					value={homeDescription}
					placeholder={strings.homeDescriptionPlaceholder}
				/>
			</>
		);
	};

	return (
		<div className='listingInfo__container'>
			{renderListingInfoCard()}
			{renderPropertyInfoCard()}
		</div>
	);
}
